<template>
  <div>
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      offset-y
      max-width="600"
    >
      <template v-slot:activator="{ on: menuOn, attrs }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              v-bind="attrs"
              v-on="{ ...menuOn, ...on }"
              small
              :class="{ 'attention-needed': !hasModifiedPreferences }"
            >
              <v-icon small>mdi-tune-vertical</v-icon>
            </v-btn>
          </template>
          <span>Personalize product scores based on your values</span>
        </v-tooltip>
      </template>

      <v-card class="ethics-menu">
        <v-card-title class="headline">
          Ethics & Political Preferences
          <v-spacer></v-spacer>
          <v-btn icon @click="menu = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-overlay
          :value="isLoading"
          absolute
        >
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
        </v-overlay>

        <v-snackbar
          v-model="showError"
          color="error"
          timeout="5000"
          top
        >
          {{ saveError }}
          <template v-slot:action="{ attrs }">
            <v-btn
              text
              v-bind="attrs"
              @click="showError = false"
            >
              Close
            </v-btn>
          </template>
        </v-snackbar>

        <!-- Anonymous User Banner -->
        <v-alert
          v-if="isAnonymous"
          type="info"
          dense
          class="mx-4 mt-2"
        >
          <div class="d-flex align-center">
            <div>
              <v-icon left>mdi-incognito</v-icon>
              You're using guest mode. Create an account to save your preferences.
            </div>
            <v-spacer></v-spacer>
            <anonymous-upgrade @upgrade-success="handleUpgradeSuccess" />
          </div>
        </v-alert>

        <!-- Persistent note about account creation for all users -->
        <v-alert
          v-if="!isAuthenticated"
          color="primary"
          dense
          class="mx-4 mt-2"
          dark
        >
          <div class="d-flex align-center">
            <div>
              <v-icon left>mdi-information</v-icon>
              Your preferences are temporary. <router-link to="/login" class="white--text font-weight-medium">Sign in or create an account</router-link> to save them permanently.
            </div>
          </div>
        </v-alert>

        <v-card-text>
          <v-tabs v-model="activeTab" grow>
            <v-tab>
              <v-icon left>mdi-handshake</v-icon>
              Ethical
            </v-tab>
            <v-tab>
              <v-icon left>mdi-gavel</v-icon>
              Political
            </v-tab>
            <v-tab>
              <v-icon left>mdi-scale-balance</v-icon>
              Weights
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab">
            <v-tab-item>
              <v-list class="preference-list">
                <v-list-item v-for="(value, key) in defaultEthicalPreferences" :key="key">
                  <v-list-item-content>
                    <v-list-item-title class="preference-title">
                      {{ formatLabel(key) }}
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on: tooltip }">
                          <v-icon small v-on="tooltip" class="ml-1">mdi-information</v-icon>
                        </template>
                        {{ getPreferenceDescription(key) }}
                      </v-tooltip>
                    </v-list-item-title>
                    <v-slider
                      v-model="localEthicalPreferences[key]"
                      min="1"
                      max="10"
                      thumb-label="always"
                      :color="getSliderColor(localEthicalPreferences[key])"
                    >
                      <template v-slot:prepend>
                        <v-icon small :color="getSliderColor(localEthicalPreferences[key])">mdi-minus</v-icon>
                      </template>
                      <template v-slot:append>
                        <v-icon small :color="getSliderColor(localEthicalPreferences[key])">mdi-plus</v-icon>
                      </template>
                    </v-slider>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-tab-item>

            <v-tab-item>
              <v-list class="preference-list">
                <v-list-item v-for="(value, key) in defaultPoliticalPreferences" :key="key">
                  <v-list-item-content>
                    <v-list-item-title class="preference-title">
                      {{ formatLabel(key) }}
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on: tooltip }">
                          <v-icon small v-on="tooltip" class="ml-1">mdi-information</v-icon>
                        </template>
                        {{ getPreferenceDescription(key) }}
                      </v-tooltip>
                    </v-list-item-title>
                    <v-slider
                      v-model="localPoliticalPreferences[key]"
                      min="1"
                      max="10"
                      thumb-label="always"
                      :color="getSliderColor(localPoliticalPreferences[key])"
                    >
                      <template v-slot:prepend>
                        <v-icon small :color="getSliderColor(localPoliticalPreferences[key])">mdi-minus</v-icon>
                      </template>
                      <template v-slot:append>
                        <v-icon small :color="getSliderColor(localPoliticalPreferences[key])">mdi-plus</v-icon>
                      </template>
                    </v-slider>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-tab-item>

            <v-tab-item>
              <v-list class="preference-list">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="preference-title">
                      Price Weight
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on: tooltip }">
                          <v-icon small v-on="tooltip" class="ml-1">mdi-information</v-icon>
                        </template>
                        How much importance do you give to the price score when evaluating products?
                      </v-tooltip>
                    </v-list-item-title>
                    <v-slider
                      v-model="localWeightPreferences.price"
                      min="1"
                      max="10"
                      thumb-label="always"
                      :color="getSliderColor(localWeightPreferences.price)"
                    >
                      <template v-slot:prepend>
                        <v-icon small :color="getSliderColor(localWeightPreferences.price)">mdi-minus</v-icon>
                      </template>
                      <template v-slot:append>
                        <v-icon small :color="getSliderColor(localWeightPreferences.price)">mdi-plus</v-icon>
                      </template>
                    </v-slider>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="preference-title">
                      Quality Weight
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on: tooltip }">
                          <v-icon small v-on="tooltip" class="ml-1">mdi-information</v-icon>
                        </template>
                        How much importance do you give to the quality score when evaluating products?
                      </v-tooltip>
                    </v-list-item-title>
                    <v-slider
                      v-model="localWeightPreferences.quality"
                      min="1"
                      max="10"
                      thumb-label="always"
                      :color="getSliderColor(localWeightPreferences.quality)"
                    >
                      <template v-slot:prepend>
                        <v-icon small :color="getSliderColor(localWeightPreferences.quality)">mdi-minus</v-icon>
                      </template>
                      <template v-slot:append>
                        <v-icon small :color="getSliderColor(localWeightPreferences.quality)">mdi-plus</v-icon>
                      </template>
                    </v-slider>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="preference-title">
                      Ethics & Political Weight
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on: tooltip }">
                          <v-icon small v-on="tooltip" class="ml-1">mdi-information</v-icon>
                        </template>
                        How much importance do you give to the ethical and political scores when evaluating products?
                      </v-tooltip>
                    </v-list-item-title>
                    <v-slider
                      v-model="localWeightPreferences.ethical"
                      min="1"
                      max="10"
                      thumb-label="always"
                      :color="getSliderColor(localWeightPreferences.ethical)"
                    >
                      <template v-slot:prepend>
                        <v-icon small :color="getSliderColor(localWeightPreferences.ethical)">mdi-minus</v-icon>
                      </template>
                      <template v-slot:append>
                        <v-icon small :color="getSliderColor(localWeightPreferences.ethical)">mdi-plus</v-icon>
                      </template>
                    </v-slider>
                  </v-list-item-content>
                </v-list-item>

                <!-- Political Weight Slider -->
                <v-list-item v-if="localPoliticalPreferences.importanceOfPoliticalContributions !== 5">
                  <v-list-item-content>
                    <v-list-item-title class="preference-title">
                      Political Weight
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on: tooltip }">
                          <v-icon small v-on="tooltip" class="ml-1">mdi-information</v-icon>
                        </template>
                        How much importance do you give to the political score when evaluating products?
                      </v-tooltip>
                    </v-list-item-title>
                    <v-slider
                      v-model="localWeightPreferences.political"
                      min="1"
                      max="10"
                      thumb-label="always"
                      color="blue"
                      :disabled="true"
                    >
                      <template v-slot:prepend>
                        <v-icon small color="blue">mdi-minus</v-icon>
                      </template>
                      <template v-slot:append>
                        <v-icon small color="blue">mdi-plus</v-icon>
                      </template>
                    </v-slider>
                    <div class="caption blue--text text--darken-1">
                      <v-icon x-small color="blue">mdi-information-outline</v-icon>
                      This weight is automatically synced with your Political Importance setting ({{ localPoliticalPreferences.importanceOfPoliticalContributions }}/10)
                    </div>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { debounce } from 'lodash';
import AnonymousUpgrade from '@/components/AnonymousUpgrade.vue';

export default {
  name: 'EthicsControl',
  components: {
    AnonymousUpgrade
  },
  data() {
    return {
      menu: false,
      activeTab: 0,
      localEthicalPreferences: {},
      localPoliticalPreferences: {},
      localWeightPreferences: {
        price: 5,
        quality: 5,
        ethical: 5,
        political: 0
      },
      saveError: '',
      showError: false,
      isSaving: false,
      isLoading: false,
      hasModifiedPreferences: false,
      defaultEthicalPreferences: {
        laborPractices: 5,
        environmentalImpact: 5,
        animalWelfare: 5,
        supplyChainTransparency: 5,
        corporateEthics: 5,
        communityImpact: 5,
        productSafety: 5,
        marketingHonesty: 5,
        culturalSensitivity: 5,
        sustainablePackaging: 5,
        energyConsumption: 5,
        dataPrivacy: 5,
        inclusivity: 5,
        plannedObsolescence: 5
      },
      defaultPoliticalPreferences: {
        importanceOfPoliticalContributions: 5,
        democraticContributions: 5,
        republicanContributions: 5,
        corporateTransparency: 5,
      },
      preferenceDescriptions: {
        laborPractices: 'Ensuring fair wages, safe working conditions, and no child or forced labor',
        environmentalImpact: 'Assessing the product\'s carbon footprint, resource usage, and sustainability',
        animalWelfare: 'Verifying that no animal testing or cruelty is involved in production',
        supplyChainTransparency: 'Seeking clear information about sourcing and manufacturing processes',
        corporateEthics: 'Evaluating the company\'s overall ethical record',
        communityImpact: 'Considering how the product\'s production and sale affect local communities',
        productSafety: 'Ensuring the product meets safety standards and is free from harmful substances',
        marketingHonesty: 'Looking for truthful advertising without misleading claims',
        culturalSensitivity: 'Avoiding products that appropriate or disrespect cultures',
        sustainablePackaging: 'Preferring minimalistic, recyclable, or biodegradable packaging',
        energyConsumption: 'Opting for energy-efficient products to reduce environmental impact',
        dataPrivacy: 'Ensuring that purchasing the product doesn\'t compromise personal information',
        inclusivity: 'Supporting brands that promote diversity and inclusivity',
        plannedObsolescence: 'Avoiding products intentionally designed with a limited lifespan',
        importanceOfPoliticalContributions: 'How important is it that a company refrains from political contributions?',
        democraticContributions: 'Importance of contributions to Democratic candidates or funds',
        republicanContributions: 'Importance of contributions to Republican candidates or funds',
        corporateTransparency: 'Importance of transparency regarding political contributions',
        price: 'How much weight to give to price when scoring products',
        quality: 'How much weight to give to quality when scoring products',
        ethical: 'How much weight to give to ethical considerations when scoring products'
      }
    };
  },
  computed: {
    ...mapState({
      storeEthicalPreferences: state => state.preferences?.ethicalPreferences || {},
      storePoliticalPreferences: state => state.preferences?.politicalPreferences || {},
      storeWeightPreferences: state => state.preferences?.weightPreferences || {}
    }),
    isAuthenticated() {
      return this.$store.getters['auth/isAuthenticated'];
    },
    isAuthInitialized() {
      return this.$store.getters['auth/isInitialized'];
    },
    isAnonymous() {
      return this.$store.getters['auth/isAnonymous'];
    }
  },
  methods: {
    ...mapActions({
      savePreferences: 'preferences/savePreferences',
      loadPreferences: 'preferences/loadPreferences'
    }),
    formatLabel(key) {
      return key
        .replace(/([A-Z])/g, ' $1')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    getPreferenceDescription(key) {
      return this.preferenceDescriptions[key] || '';
    },
    getSliderColor(value) {
      if (value <= 3) return 'red';
      if (value <= 7) return 'orange';
      return 'green';
    },
    initializeLocalPreferences() {
      this.localEthicalPreferences = { ...this.defaultEthicalPreferences, ...this.storeEthicalPreferences };
      this.localPoliticalPreferences = { ...this.defaultPoliticalPreferences, ...this.storePoliticalPreferences };
      this.localWeightPreferences = { ...this.localWeightPreferences, ...this.storeWeightPreferences };
      
      // Check if any preferences have been modified from their defaults
      this.checkIfPreferencesModified();
    },
    checkIfPreferencesModified() {
      // Check if any preferences deviate from the default value of 5
      const isEthicalModified = Object.values(this.localEthicalPreferences).some(value => value !== 5);
      const isPoliticalModified = Object.values(this.localPoliticalPreferences).some(value => value !== 5);
      const isWeightModified = Object.values(this.localWeightPreferences).some(value => value !== 5);
      
      // Check if there are any saved preferences in the store that deviate from 5
      const hasStoredPreferences = 
        Object.values(this.storeEthicalPreferences).some(value => value !== 5) || 
        Object.values(this.storePoliticalPreferences).some(value => value !== 5) ||
        Object.values(this.storeWeightPreferences).some(value => value !== 5);
      
      this.hasModifiedPreferences = isEthicalModified || isPoliticalModified || isWeightModified || hasStoredPreferences;
      
      // Store the state in localStorage to persist across sessions
      if (this.hasModifiedPreferences) {
        localStorage.setItem('hasModifiedPreferences', 'true');
      } else {
        localStorage.removeItem('hasModifiedPreferences');
      }
    },
    async initializePreferences() {
      try {
        this.isLoading = true;
        console.log('EthicsControl: Initializing preferences');
        
        // For non-authenticated users, try to load from localStorage
        if (!this.isAuthenticated) {
          console.log('EthicsControl: Loading preferences from localStorage for non-authenticated user');
          try {
            const savedPrefs = localStorage.getItem('userPreferences');
            if (savedPrefs) {
              const parsedPrefs = JSON.parse(savedPrefs);
              console.log('EthicsControl: Found preferences in localStorage:', parsedPrefs);
              
              if (parsedPrefs.ethical) {
                this.localEthicalPreferences = { ...this.defaultEthicalPreferences, ...parsedPrefs.ethical };
                // Update store
                Object.entries(parsedPrefs.ethical).forEach(([key, value]) => {
                  this.$store.commit('preferences/setEthicalPreference', { key, value });
                });
              }
              
              if (parsedPrefs.political) {
                this.localPoliticalPreferences = { ...this.defaultPoliticalPreferences, ...parsedPrefs.political };
                // Update store
                Object.entries(parsedPrefs.political).forEach(([key, value]) => {
                  this.$store.commit('preferences/setPoliticalPreference', { key, value });
                });
              }
              
              if (parsedPrefs.weights) {
                this.localWeightPreferences = { ...this.localWeightPreferences, ...parsedPrefs.weights };
                // Update store
                Object.entries(parsedPrefs.weights).forEach(([key, value]) => {
                  this.$store.commit('preferences/setWeightPreference', { key, value });
                });
              }
              
              this.checkIfPreferencesModified();
            } else {
              console.log('EthicsControl: No preferences found in localStorage');
              this.initializeLocalPreferences();
            }
          } catch (error) {
            console.error('EthicsControl: Error loading preferences from localStorage:', error);
            this.initializeLocalPreferences();
          }
          this.isLoading = false;
          return;
        }
        
        // For authenticated users, load from the store/database
        if (this.isAuthenticated) {
          console.log('EthicsControl: Loading preferences for authenticated user');
          const result = await this.loadPreferences();
          
          if (!result || !result.success) {
            console.log('EthicsControl: Initial preference load was not successful, will retry once');
            // Add a short delay and try again in case of race condition
            setTimeout(async () => {
              try {
                console.log('EthicsControl: Retrying preference load');
                await this.loadPreferences();
              } catch (retryError) {
                console.error('EthicsControl: Retry failed:', retryError);
              } finally {
                this.initializeLocalPreferences();
                this.isLoading = false;
              }
            }, 1000);
            return;
          }

          // Check if there are any saved preferences that deviate from default value of 5
          const hasStoredPreferences = 
            Object.values(this.storeEthicalPreferences).some(value => value !== 5) || 
            Object.values(this.storePoliticalPreferences).some(value => value !== 5) ||
            Object.values(this.storeWeightPreferences).some(value => value !== 5);
          
          // If we have non-default stored preferences, disable the animation
          if (hasStoredPreferences) {
            this.hasModifiedPreferences = true;
            localStorage.setItem('hasModifiedPreferences', 'true');
          } else {
            this.hasModifiedPreferences = false;
            localStorage.removeItem('hasModifiedPreferences');
          }
        }
      } catch (error) {
        console.error('EthicsControl: Failed to load preferences:', error);
        this.saveError = 'Failed to load preferences. Please try again later.';
        this.showError = true;
      } finally {
        this.isLoading = false;
        this.initializeLocalPreferences();
      }
    },
    debouncedSave: debounce(async function() {
      // For authenticated users, save to Firestore
      if (this.isAuthenticated) {
        try {
          this.isSaving = true;
          this.saveError = null;
          this.showError = false;
          await this.savePreferences();
        } catch (error) {
          console.error('Failed to save preferences:', error);
          this.saveError = 'Failed to save preferences. Please try again later.';
          this.showError = true;
          
          this.initializeLocalPreferences();
        } finally {
          this.isSaving = false;
        }
      } 
      // For non-authenticated users, save to localStorage
      else {
        try {
          const prefsToSave = {
            ethical: this.localEthicalPreferences,
            political: this.localPoliticalPreferences,
            weights: this.localWeightPreferences
          };
          localStorage.setItem('userPreferences', JSON.stringify(prefsToSave));
          console.log('Saved preferences to localStorage:', prefsToSave);
        } catch (error) {
          console.error('Failed to save preferences to localStorage:', error);
        }
      }
    }, 1000),
    async showLoginDialog() {
      // If not authenticated, sign in anonymously first
      if (!this.isAuthenticated) {
        try {
          await this.$store.dispatch('auth/signInAnonymously');
          // After anonymous sign-in, open the menu
          this.menu = true;
        } catch (error) {
          console.error('Anonymous sign-in failed:', error);
          this.saveError = 'Failed to sign in. Please try again.';
          this.showError = true;
        }
      }
    },
    handleUpgradeSuccess() {
      // Refresh preferences after account upgrade
      this.$store.dispatch('preferences/loadPreferences');
    },
    handleResize() {
      // Update mobile detection on resize
      this.isMobile();
    },
    isMobile() {
      return window.innerWidth < 600;
    },
    goToLogin() {
      this.$router.push('/login');
    }
  },
  watch: {
    storeEthicalPreferences: {
      handler(newVal) {
        this.localEthicalPreferences = { ...this.defaultEthicalPreferences, ...newVal };
      },
      deep: true
    },
    storePoliticalPreferences: {
      handler(newVal) {
        this.localPoliticalPreferences = { ...this.defaultPoliticalPreferences, ...newVal };
      },
      deep: true
    },
    storeWeightPreferences: {
      handler(newVal) {
        this.localWeightPreferences = { ...this.localWeightPreferences, ...newVal };
      },
      deep: true
    },
    localEthicalPreferences: {
      handler(newVal) {
        // Debug: Log the current values before making any changes
        console.log('Before updating ethical preferences:', {
          store: JSON.stringify(this.$store.state.preferences.ethicalPreferences),
          local: JSON.stringify(newVal)
        });
        
        // Update store regardless of authentication status
        Object.entries(newVal).forEach(([key, value]) => {
          this.$store.commit('preferences/setEthicalPreference', { key, value });
        });
        
        // Debug: Log after updating
        setTimeout(() => {
          console.log('After updating ethical preferences:', {
            storeEthical: JSON.stringify(this.$store.state.preferences.ethicalPreferences)
          });
        }, 100);
        
        this.debouncedSave();
      },
      deep: true
    },
    localPoliticalPreferences: {
      handler(newVal) {
        // Debug: Log the current values before making any changes
        console.log('Before updating political preferences:', {
          store: JSON.stringify(this.$store.state.preferences.politicalPreferences),
          local: JSON.stringify(newVal)
        });
        
        // Update store regardless of authentication status
        Object.entries(newVal).forEach(([key, value]) => {
          this.$store.commit('preferences/setPoliticalPreference', { key, value });
        });
        
        // Always sync political weight with importanceOfPoliticalContributions
        if (newVal.importanceOfPoliticalContributions !== 5) {
          // Set political weight to match the importance of political contributions
          this.localWeightPreferences.political = newVal.importanceOfPoliticalContributions;
          console.log('Setting political weight to:', newVal.importanceOfPoliticalContributions);
          
          // Explicitly update the weight preference in the store
          this.$store.commit('preferences/setWeightPreference', { 
            key: 'political', 
            value: newVal.importanceOfPoliticalContributions 
          });
        } else {
          // If political importance is reset to default, reset weight to 0
          this.localWeightPreferences.political = 0;
          console.log('Resetting political weight to 0');
          
          // Explicitly update the weight preference in the store
          this.$store.commit('preferences/setWeightPreference', { key: 'political', value: 0 });
        }
        
        // Debug: Log after updating
        setTimeout(() => {
          console.log('After updating political preferences:', {
            storeWeights: JSON.stringify(this.$store.state.preferences.weightPreferences),
            storePolitical: JSON.stringify(this.$store.state.preferences.politicalPreferences)
          });
        }, 100);
        
        this.debouncedSave();
      },
      deep: true
    },
    localWeightPreferences: {
      handler(newVal) {
        // Update store regardless of authentication status
        Object.entries(newVal).forEach(([key, value]) => {
          this.$store.commit('preferences/setWeightPreference', { key, value });
        });
        this.debouncedSave();
      },
      deep: true
    },
    isAuthenticated: {
      immediate: true,
      handler(newVal) {
        console.log('EthicsControl: Authentication changed:', newVal);
        if (this.isAuthInitialized && newVal) {
          console.log('EthicsControl: User authenticated and initialized, loading preferences');
          this.initializePreferences();
        } else if (this.isAuthInitialized) {
          console.log('EthicsControl: User not authenticated, resetting to defaults');
          this.initializeLocalPreferences();
        }
      }
    },
    isAuthInitialized: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.isAuthenticated) {
          this.initializePreferences();
        }
      }
    },
    menu(val) {
      if (val && !this.isAuthenticated) {
        // If menu is opened and user is not authenticated, sign in anonymously
        this.showLoginDialog();
      }
    }
  },
  async created() {
    console.log('EthicsControl: Component created, auth status:', this.isAuthenticated);
    // Check if preferences were modified in a previous session
    this.hasModifiedPreferences = localStorage.getItem('hasModifiedPreferences') === 'true';
    await this.initializePreferences();
  },
  mounted() {
    // Add resize listener for mobile detection
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    // Remove resize listener
    window.removeEventListener('resize', this.handleResize);
  }
};
</script>

<style scoped>
.ethics-menu {
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
}

.preference-list {
  padding: 16px;
}

.v-list-item {
  padding: 8px 0;
}

.preference-title {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  margin-bottom: 4px;
}

.v-slider {
  margin-top: 8px;
}

.v-tab {
  text-transform: none;
}

@keyframes attention {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }
}

.attention-needed {
  animation: attention 2s ease-in-out infinite;
  position: relative;
}

.attention-needed::after {
  content: '';
  position: absolute;
  top: -4px;
  right: -4px;
  bottom: -4px;
  left: -4px;
  border: 2px solid currentColor;
  border-radius: 50%;
  opacity: 0;
  animation: ripple 2s ease-in-out infinite;
}

@keyframes ripple {
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  50% {
    opacity: 0.3;
  }
  100% {
    transform: scale(1.2);
    opacity: 0;
  }
}
</style> 